import React from "react";
import { Link } from "react-router-dom";
import { APP_NAME, SUPPORT_EMAIL } from "../constants/expiry";
import { SentrilogIcon } from "../components/Icons";

const EFFECTIVE_DATE = "March 21, 2026";
const COMPANY_NAME = "Sentrilog";
const COMPANY_EMAIL = SUPPORT_EMAIL;

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-[#111827] mb-4">{title}</h2>
    <div className="text-[#374151] text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const RefundPolicy: React.FC = () => (
  <div className="min-h-screen bg-[#F9FAFB]">
    {/* Nav */}
    <header className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
            <SentrilogIcon className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-[#111827] text-lg">{APP_NAME}</span>
        </Link>
        <Link
          to="/"
          className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
        >
          ← Back to home
        </Link>
      </div>
    </header>

    {/* Content */}
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-3xl font-extrabold text-[#111827] mb-2">
          Refund Policy
        </h1>
        <p className="text-sm text-[#6B7280]">
          Effective date: {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] px-8 py-10">
        <p className="text-[#374151] text-sm leading-relaxed mb-8">
          Thank you for subscribing to {COMPANY_NAME}. We want you to be
          completely satisfied with our service. This Refund Policy explains
          when and how you may request a refund for payments made to{" "}
          {COMPANY_NAME} ("<strong>we</strong>", "<strong>our</strong>", or "
          <strong>us</strong>").
        </p>

        <Section title="1. Subscription Plans">
          <p>
            {COMPANY_NAME} offers the following paid subscription tiers: Pro
            and Business. Subscriptions are billed monthly (or annually where
            offered) and provide access to features described on our{" "}
            <a href="/pricing" className="text-[#2563EB] underline underline-offset-2">
              pricing page
            </a>
            .
          </p>
          <p>
            A free tier is also available at no cost and is not subject to this
            refund policy.
          </p>
        </Section>

        <Section title="2. 7-Day Money-Back Guarantee">
          <p>
            We offer a <strong>7-day money-back guarantee</strong> on all new
            paid subscriptions (first payment only). If you subscribe to a paid
            plan and are not satisfied within the first 7 calendar days of your
            initial payment, you may request a full refund with no questions
            asked.
          </p>
          <p>
            To request a refund under this guarantee, email us at{" "}
            <a
              href={`mailto:${COMPANY_EMAIL}?subject=Refund%20Request`}
              className="text-[#2563EB] underline underline-offset-2"
            >
              {COMPANY_EMAIL}
            </a>{" "}
            with the subject line "<strong>Refund Request</strong>" within 7
            days of your initial payment. Please include the email address
            associated with your account.
          </p>
        </Section>

        <Section title="3. Eligibility">
          <p>Refunds are available when:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              The refund request is made within 7 days of the first charge for
              a new subscription.
            </li>
            <li>
              The request is for the most recent subscription payment only (not
              historical billing periods).
            </li>
          </ul>

          <p className="mt-3">Refunds are <strong>not</strong> available for:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              Renewal charges after the initial 7-day period has passed.
            </li>
            <li>
              Partial months or unused days remaining in a billing period after
              cancellation.
            </li>
            <li>
              Accounts that have received a refund for a previous subscription
              to {COMPANY_NAME}.
            </li>
            <li>
              Accounts where there is evidence of abuse, fraud, or violation of
              our{" "}
              <a href="/terms" className="text-[#2563EB] underline underline-offset-2">
                Terms of Service
              </a>
              .
            </li>
          </ul>
        </Section>

        <Section title="4. Cancellation">
          <p>
            You may cancel your subscription at any time from your account
            settings or by contacting us. Cancellation stops future billing but
            does not automatically trigger a refund. Your access to paid
            features continues until the end of the current billing period.
          </p>
          <p>
            To cancel, sign in to your account, navigate to Settings → Billing,
            and click "Cancel subscription". You may also email us at{" "}
            <a
              href={`mailto:${COMPANY_EMAIL}?subject=Cancel%20Subscription`}
              className="text-[#2563EB] underline underline-offset-2"
            >
              {COMPANY_EMAIL}
            </a>{" "}
            and we will process the cancellation within one business day.
          </p>
        </Section>

        <Section title="5. Refund Process">
          <p>
            Approved refunds are processed to the original payment method
            within <strong>5–10 business days</strong>, depending on your card
            issuer. We will send a confirmation email once the refund has been
            initiated on our end.
          </p>
          <p>
            If you have not received your refund after 10 business days, please
            contact your bank or card issuer first. If the issue persists,
            contact us at{" "}
            <a
              href={`mailto:${COMPANY_EMAIL}`}
              className="text-[#2563EB] underline underline-offset-2"
            >
              {COMPANY_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section title="6. Disputed Charges">
          <p>
            If you believe you have been charged in error, please contact us
            before filing a dispute with your bank. Most billing issues can be
            resolved quickly, and chargebacks can result in account suspension
            while under review.
          </p>
        </Section>

        <Section title="7. Plan Downgrades">
          <p>
            If you downgrade from a paid plan to the Free plan, you will retain
            access to paid features until the end of your current billing
            period. No refund is issued for the remaining unused days unless
            the request falls within the 7-day money-back guarantee.
          </p>
        </Section>

        <Section title="8. Enterprise Plans">
          <p>
            Refund terms for Enterprise subscriptions are governed by the
            individual agreement between {COMPANY_NAME} and the customer.
            Please refer to your contract or contact us for details.
          </p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>
            We reserve the right to modify this Refund Policy at any time.
            Changes will be posted on this page with an updated effective date.
            Continued use of our service after changes are posted constitutes
            acceptance of the updated policy.
          </p>
        </Section>

        <Section title="10. Contact Us">
          <p>
            If you have any questions about this Refund Policy or want to
            request a refund, please contact us:
          </p>
          <div className="mt-3 p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] text-sm">
            <p className="font-semibold text-[#111827]">{COMPANY_NAME}</p>
            <p>
              Email:{" "}
              <a
                href={`mailto:${COMPANY_EMAIL}`}
                className="text-[#2563EB] underline underline-offset-2"
              >
                {COMPANY_EMAIL}
              </a>
            </p>
            <p>
              Website:{" "}
              <a
                href="https://sentrilog.com"
                className="text-[#2563EB] underline underline-offset-2"
              >
                sentrilog.com
              </a>
            </p>
          </div>
        </Section>
      </div>
    </main>

    {/* Footer */}
    <footer className="bg-[#111827] text-[#6B7280] py-8 mt-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
        <p>© {new Date().getFullYear()} {APP_NAME}. All rights reserved.</p>
        <div className="flex items-center gap-4">
          <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
          <a href="/terms" className="hover:text-white transition-colors">Terms</a>
          <a href="/refund" className="hover:text-white transition-colors">Refund Policy</a>
          <a href="/cookie-policy" className="hover:text-white transition-colors">Cookies</a>
          <a href="/pricing" className="hover:text-white transition-colors">Pricing</a>
        </div>
      </div>
    </footer>
  </div>
);

export default RefundPolicy;
